import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '../components/button.js';
import { PlatformMark } from '../components/chip.js';
import { SeverityRow, StatusBadge } from '../components/status.js';
import { PageHeader, ScreenError } from '../app/layout.js';
import { AppGrid } from '../app/app-grid.js';
import { useAccounts } from '../data/accounts.js';
import { usePosts, type PostListItem } from '../data/posts.js';
import { useTimezone } from '../data/session.js';
import {
  connectionStatusLabel,
  needsAttention,
  postStatusLabel,
  targetStatusLabel,
} from '../lib/status-labels.js';
import { formatWhen } from '../lib/time.js';
import { messageOf } from '../lib/errors.js';
import { headlineNote, type Account } from '../data/types.js';

/**
 * The screen the app opens on (PLAN §13.2).
 *
 * Ordered by what the owner came to find out, not by what is easiest to query:
 * what is broken, what is happening right now, what is coming, what happened.
 * The first section is empty on a good day, and that is the point.
 */
export function DashboardScreen() {
  const navigate = useNavigate();
  const posts = usePosts({});
  const accounts = useAccounts();
  const timezone = useTimezone();

  // Memoised so the `?? []` fallback does not mint a new array every render
  // and invalidate everything derived from it.
  const all = useMemo(() => posts.data ?? [], [posts.data]);

  // "Up next" is a window on the clock, so the clock has to be state rather
  // than a reading taken during render. Ticking it every minute also keeps the
  // relative hints ("in 3 h") honest on a screen left open all evening.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 60_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const broken = all.filter((post) => post.targets.some((target) => needsAttention(target.status)));
  const publishing = all.filter((post) => post.status === 'publishing');
  const upNext = useMemo(
    () =>
      all
        .filter(
          (post) =>
            post.status === 'scheduled' &&
            post.scheduledAt !== null &&
            new Date(post.scheduledAt).getTime() > now &&
            new Date(post.scheduledAt).getTime() < now + 7 * 86_400_000,
        )
        .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? '')),
    [all, now],
  );
  const recent = all.slice(0, 10);

  const troubledAccounts = (accounts.data ?? []).filter(
    (account) => account.connectionStatus !== 'active',
  );

  // The dashboard is the one screen that makes a *claim* when it is empty —
  // "Nothing is waiting on you." On a failed request that claim was a guess,
  // and the wrong one: it told an owner there were no problems at the exact
  // moment the app could not tell. The header stays so the page is still
  // recognisable; everything that would assert something does not render.
  if (posts.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 pb-6">
        <PageHeader title="Dashboard" />
        <ScreenError
          message={messageOf(posts.error)}
          busy={posts.isFetching}
          onRetry={() => void posts.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-6">
      {/* The grid carries New post on a phone, and the bar at the bottom
          carries it again — a third button in the header would be three
          ways to do one thing on one screen. */}
      <PageHeader
        title="Dashboard"
        subtitle={posts.isPending ? 'Loading…' : undefined}
        action={
          /* Wrapped rather than given `hidden md:inline-flex`: the button
             already sets `inline-flex`, and two display utilities on one
             element resolve by stylesheet order rather than by intent. */
          <span className="hidden md:block">
            <Button
              variant="primary"
              onClick={() => {
                void navigate('/compose');
              }}
            >
              New post
            </Button>
          </span>
        }
      />

      <AppGrid />

      <Section
        title="Needs attention"
        empty={
          broken.length === 0 && troubledAccounts.length === 0
            ? 'Nothing is waiting on you.'
            : undefined
        }
      >
        {troubledAccounts.map((account) => (
          <AccountProblemRow key={account.id} account={account} />
        ))}
        {broken.map((post) => (
          <PostRow key={post.id} post={post} timezone={timezone} onlyProblemTargets />
        ))}
      </Section>

      {publishing.length > 0 && (
        <Section title="Publishing now">
          {publishing.map((post) => (
            <PostRow key={post.id} post={post} timezone={timezone} />
          ))}
        </Section>
      )}

      <Section
        title="Up next"
        subtitle="Next 7 days"
        empty={upNext.length === 0 ? 'Nothing scheduled this week.' : undefined}
      >
        {upNext.map((post) => (
          <PostRow key={post.id} post={post} timezone={timezone} />
        ))}
      </Section>

      <Section
        title="Recent"
        empty={
          recent.length === 0 ? 'No posts yet. Tap "New post" to make the first one.' : undefined
        }
      >
        {recent.map((post) => (
          <PostRow key={post.id} post={post} timezone={timezone} />
        ))}
      </Section>
    </div>
  );
}

/**
 * A titled block on the dashboard.
 *
 * The heading used to be inset 16px while the list below it ran edge to edge,
 * so every card started to the left of its own title. One container now, with
 * the same padding throughout, and tighter gaps: the old 20px between blocks
 * plus 12px of padding inside each empty state left the screen looking mostly
 * like air.
 */
function Section({
  title,
  subtitle,
  empty,
  children,
}: {
  title: string;
  subtitle?: string | undefined;
  empty?: string | undefined;
  children?: React.ReactNode | undefined;
}) {
  return (
    <section className="mb-3">
      <div className="flex items-baseline gap-2 pb-1.5">
        <h2 className="font-mono text-2xs font-semibold tracking-[0.1em] text-muted uppercase">
          {title}
        </h2>
        {subtitle && <span className="text-2xs text-faint">{subtitle}</span>}
      </div>

      {empty ? (
        <p className="rounded-aw border border-line bg-surface px-3 py-2.5 text-sm text-muted">
          {empty}
        </p>
      ) : (
        <div className="overflow-hidden rounded-aw border border-line bg-surface shadow-aw-card">
          {children}
        </div>
      )}
    </section>
  );
}

function AccountProblemRow({ account }: { account: Account }) {
  const { tone, label } = connectionStatusLabel(account.connectionStatus);

  return (
    <SeverityRow tone={tone}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <PlatformMark platform={account.platform} />
          <span className="truncate font-mono text-sm text-ink">{account.handle}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge tone={tone}>{label}</StatusBadge>
          <Link
            to="/accounts"
            className="text-xs font-semibold text-accent underline underline-offset-2"
          >
            Fix
          </Link>
        </div>
      </div>
      {headlineNote(account) && <p className="mt-1 text-xs text-muted">{headlineNote(account)}</p>}
    </SeverityRow>
  );
}

function PostRow({
  post,
  timezone,
  onlyProblemTargets = false,
}: {
  post: PostListItem;
  timezone: string;
  onlyProblemTargets?: boolean;
}) {
  const status = postStatusLabel(post.status);
  const targets = onlyProblemTargets
    ? post.targets.filter((target) => needsAttention(target.status))
    : post.targets;

  const when = post.scheduledAt ?? post.updatedAt;

  return (
    <SeverityRow tone={status.tone}>
      <Link to={`/posts/${post.id}`} className="block">
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-md font-medium text-ink">
            {post.hook || 'Untitled draft'}
          </p>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </div>

        <p className="mt-0.5 font-mono text-2xs text-faint">{formatWhen(when, timezone)}</p>

        {targets.length > 0 && (
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {targets.map((target) => {
              const label = targetStatusLabel(target.status, target.platform);
              return (
                <li key={target.accountId}>
                  <span className="inline-flex items-center gap-1.5">
                    <PlatformMark platform={target.platform} size={14} />
                    <StatusBadge tone={label.tone}>{target.handle}</StatusBadge>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Link>
    </SeverityRow>
  );
}
